import React, { useState } from 'react';
import { ArrowRight, Check } from 'lucide-react';
import { motion } from 'framer-motion';

const EmailOptIn: React.FC<{ className?: string }> = ({ className = '' }) => {
  const [email, setEmail] = useState('');
  const [submitted, setSubmitted] = useState(false);
  const [error, setError] = useState('');
  const [isFocused, setIsFocused] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  
  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!email) {
      setError('Please enter your email');
      return;
    }
    
    if (!/\S+@\S+\.\S+/.test(email)) {
      setError('Please enter a valid email');
      return;
    }

    setIsSubmitting(true);
    
    try {
      const formData = new URLSearchParams();
      formData.append('email', email);
      formData.append('timestamp', new Date().toISOString());
      formData.append('source', window.location.hostname);

      const response = await fetch('https://script.google.com/macros/s/AKfycbzwVvXu8phQyeMDWpkD05uvl_nQ5pTGNWqR7SaR_KXH4uAeWkF0p4JLg1wjB9GGBdAO/exec?' + formData.toString(), {
        method: 'GET',
        mode: 'no-cors',
      });

      setSubmitted(true);
      setError('');
    } catch (err) {
      setError('Failed to submit. Please try again.');
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className={className}>
      {submitted ? (
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="bg-white/5 backdrop-blur-sm p-6 rounded-xl border border-primary/30"
        >
          <Check className="w-8 h-8 text-primary mx-auto mb-3" />
          <p className="text-white/90 text-center">
            Thank you for joining the waitlist. You're one step closer to reclaiming your attention.
          </p>
        </motion.div>
      ) : (
        <div className="relative">
          <form onSubmit={handleSubmit} className="flex flex-col sm:flex-row gap-3 justify-center">
            <div className="flex-grow relative max-w-md">
              <motion.div
                animate={{
                  boxShadow: isFocused 
                    ? '0 0 25px rgba(199, 161, 107, 0.3)' 
                    : '0 0 0 rgba(199, 161, 107, 0)'
                }}
                transition={{ duration: 0.3 }}
                className="relative rounded-xl overflow-hidden"
              >
                <input
                  type="email"
                  placeholder="Enter your email"
                  className="w-full h-12 px-6 bg-white/5 backdrop-blur-sm border border-white/10 text-white placeholder-white/40 focus:outline-none focus:border-primary/50 transition-all duration-300 rounded-xl"
                  value={email}
                  onChange={(e) => {
                    setEmail(e.target.value);
                    if (error) setError('');
                  }}
                  onFocus={() => setIsFocused(true)}
                  onBlur={() => setIsFocused(false)}
                  disabled={isSubmitting}
                />
              </motion.div>
              {error && (
                <p className="absolute -bottom-6 left-0 text-red-500 text-sm">
                  {error}
                </p>
              )}
            </div>
            <button
              type="submit"
              disabled={isSubmitting}
              className="h-12 px-6 bg-primary text-dark font-medium rounded-xl hover:bg-primary/90 transition-all duration-300 flex items-center justify-center whitespace-nowrap disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {isSubmitting ? (
                <span className="flex items-center">
                  <span className="w-4 h-4 border-2 border-dark/20 border-t-dark animate-spin rounded-full mr-2"></span>
                  Submitting...
                </span>
              ) : (
                <>
                  Reserve Your Spot
                  <ArrowRight className="w-4 h-4 ml-2" />
                </>
              )}
            </button>
          </form>
          <p className="text-white/40 text-sm text-center mt-4">
            Limited initial release. No spam, just updates.
          </p>
        </div>
      )}
    </div>
  );
};

export default EmailOptIn;